import * as React from 'react';
import { Alert, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import { Dropdown, selectUser, useCacheQuery, useCacheRecord } from 'gossamer-front-end';
import { BookingPaymentTerms, InstitutionView, UserView } from 'cb-model';
import { PERSONAL_INSTITUTION_ID } from 'cb-unified';
import { AlertOctagon } from 'lucide-react';
import { EventDetailsSectionLayout } from './EventDetailsSectionLayout';
import { EventDetails, HandleEventDetailsChange } from './eventDetailsFormTypes';

type Props<T extends EventDetails> = {
  eventDetails: T;
  institutionId: number;
  isPaidEvent: boolean;
  lockPaidEventChanges?: boolean;
  onIsPaidEventChange: (next: boolean) => void;
  handleDetailsChange: HandleEventDetailsChange<T>;
};

export function EventPaidDetailsSection<T extends EventDetails>(props: Props<T>): React.JSX.Element {
  const { eventDetails, institutionId, isPaidEvent, lockPaidEventChanges, onIsPaidEventChange, handleDetailsChange } =
    props;
  const user = useSelector(selectUser);

  const institutionRecord = useCacheRecord(InstitutionView, String(institutionId), !institutionId);
  const userRecord = useCacheRecord(UserView, user?.userId, institutionId !== PERSONAL_INSTITUTION_ID || !user?.userId);

  const bookingPaymentTermsQuery = useCacheQuery(
    BookingPaymentTerms,
    {
      where: {
        and: [{ fieldId: 'institution_id', operator: 'eq', value: PERSONAL_INSTITUTION_ID }],
      },
      limit: 100,
    },
    institutionId !== PERSONAL_INSTITUTION_ID,
  );

  const stripeSetup =
    institutionId === PERSONAL_INSTITUTION_ID
      ? !!(userRecord.data as any)?.stripe_connect_payouts_enabled
      : !!(institutionRecord.data as any)?.stripe_connect_payouts_enabled;

  const bookingPaymentTermsId = (eventDetails as any)?.booking_payment_terms_id as number | null | undefined;
  const bookingPaymentTermsSetup =
    institutionId === PERSONAL_INSTITUTION_ID || !!(eventDetails as any)?.booking_payment_terms_id;

  return (
    <EventDetailsSectionLayout
      title="Paid event"
      subtitle="Decide up front whether this is a paid event so the rest of the form can guide ticket pricing and setup correctly."
    >
      <Form.Group controlId="is-paid-event">
        <Form.Check
          type="switch"
          id="is-paid-event-switch"
          label="Is this a paid event?"
          checked={isPaidEvent}
          disabled={!!lockPaidEventChanges}
          onChange={(e) => onIsPaidEventChange(e.currentTarget.checked)}
        />
        <Form.Text muted>
          Paid events can include free tickets, but must include at least one ticket with a price greater than zero.
        </Form.Text>
        {!!lockPaidEventChanges && (
          <Form.Text className="text-muted d-block">
            The paid-event toggle cannot be changed once paid tickets exist. Ticket prices can still be updated below.
          </Form.Text>
        )}
      </Form.Group>

      {isPaidEvent && !stripeSetup && !!institutionId && (
        <div className="text-muted" style={{ fontSize: 13 }}>
          <Alert variant="danger" className="d-flex align-items-start gap-2 py-2 px-3 mb-0">
            <AlertOctagon aria-hidden="true" size={18} className="flex-shrink-0 mt-1" />
            <div style={{ fontSize: 13 }}>
              Paid events require Stripe Connect payouts to be enabled. You cannot continue with a paid event until you{' '}
              {institutionId === PERSONAL_INSTITUTION_ID ? (
                <a href="/website/account/profile">complete the Stripe Connect flow in your account settings</a>
              ) : (
                <Link to="/admin/payments/institution-summary">
                  complete the Stripe Connect flow in the institution admin dashboard
                </Link>
              )}
              .
            </div>
          </Alert>
        </div>
      )}

      {isPaidEvent && !bookingPaymentTermsSetup && (
        <div className="text-muted" style={{ fontSize: 13 }}>
          <Alert variant="danger" className="d-flex align-items-start gap-2 py-2 px-3 mb-0">
            <AlertOctagon aria-hidden="true" size={18} className="flex-shrink-0 mt-1" />
            <div style={{ fontSize: 13 }}>
              Paid events require Booking Payment Terms to be set up.{' '}
              <Link to="/admin/event-profile/search">
                Set up Booking Payment Terms in the Event Profile(s) you wish to use
              </Link>
              .
            </div>
          </Alert>
        </div>
      )}
      {isPaidEvent && institutionId === PERSONAL_INSTITUTION_ID && (
        <Form.Group controlId="booking-payment-terms">
          <Form.Label>Booking payment terms</Form.Label>
          <Dropdown
            mandatory={false}
            onItemSelected={(value) => {
              const parsed = value ? parseInt(value, 10) : NaN;
              handleDetailsChange('booking_payment_terms_id' as keyof T, Number.isFinite(parsed) ? parsed : null);
            }}
            options={(bookingPaymentTermsQuery.data || []).map((t: any) => ({ id: String(t.id), label: t.title }))}
            value={bookingPaymentTermsId != null ? String(bookingPaymentTermsId) : ''}
          />
        </Form.Group>
      )}
    </EventDetailsSectionLayout>
  );
}
